/**
 * databaseService.js - Serviço Principal do Banco de Dados
 * 
 * Centraliza todas as operações de banco de dados do QuizFlow:
 * - Conquistas (achievements)
 * - Atividades do usuário
 * - Notificações
 * - Sessões de estudo
 * - Objetivos
 * - Estatísticas
 */

import { supabase } from '../lib/supabase';
import { getCachedUser } from './userCacheService';
import * as FileSystem from 'expo-file-system/legacy';
import { Platform } from 'react-native';

// ==================== CONQUISTAS (ACHIEVEMENTS) ====================

/**
 * Obtém todas as conquistas disponíveis
 */
export const getAllAchievements = async () => {
    try {
        const { data, error } = await supabase
            .from('achievements')
            .select('id, code, icon, name, description, rarity, xp_reward, is_secret, unlock_condition, sort_order')
            .order('sort_order', { ascending: true })
            .limit(50); // Limite para performance

        if (error) throw error;
        return data || [];
    } catch (error) {

        return [];
    }
};

/**
 * Obtém conquistas do usuário (desbloqueadas e em progresso)
 */
export const getUserAchievements = async () => {
    try {
        const user = await getCachedUser();
        if (!user) return { unlocked: [], inProgress: [] };

        // Buscar conquistas desbloqueadas do usuário (otimizado: colunas específicas)
        const { data: userAchievements, error: userError } = await supabase
            .from('user_achievements')
            .select('id, achievement_id, unlocked_at, progress')
            .eq('user_id', user.id)
            .order('unlocked_at', { ascending: false })
            .limit(50); // Limite para performance

        if (userError) throw userError;

        // Buscar todas as conquistas
        const { data: allAchievements } = await supabase
            .from('achievements')
            .select('id, icon, name, description, rarity, xp_reward, is_secret, sort_order')
            .order('sort_order', { ascending: true });

        // Criar map de conquistas por ID
        const achievementMap = new Map(allAchievements?.map(a => [a.id, a]) || []);

        // Separar em desbloqueadas e em progresso
        const unlockedIds = new Set(userAchievements?.map(ua => ua.achievement_id) || []);

        const unlocked = userAchievements?.map(ua => {
            const achievement = achievementMap.get(ua.achievement_id);
            if (!achievement) return null;
            return {
                id: achievement.id,
                icon: achievement.icon,
                name: achievement.name,
                desc: achievement.description,
                rarity: achievement.rarity,
                unlocked: true,
                date: new Date(ua.unlocked_at).toLocaleDateString('pt-BR'),
                xpReward: achievement.xp_reward,
            };
        }).filter(Boolean) || [];

        const inProgress = allAchievements
            ?.filter(a => !unlockedIds.has(a.id) && !a.is_secret)
            .map(a => ({
                id: a.id,
                icon: a.icon,
                name: a.name,
                desc: a.description,
                rarity: a.rarity,
                unlocked: false,
                progress: 0,
                xpReward: a.xp_reward,
            })) || [];

        return { unlocked, inProgress };
    } catch (error) {

        return { unlocked: [], inProgress: [] };
    }
};

/**
 * Obtém conquistas formatadas para o ProfileScreen
 * Retorna no mesmo formato do mock ACHIEVEMENTS
 */
export const getAchievementsForProfile = async () => {
    try {
        const user = await getCachedUser();
        if (!user) return [];

        // Buscar conquistas do usuário (otimizado: com limite)
        const { data: userAchievements } = await supabase
            .from('user_achievements')
            .select('achievement_id, unlocked_at')
            .eq('user_id', user.id)
            .limit(100);

        const unlockedMap = new Map(
            userAchievements?.map(ua => [ua.achievement_id, ua]) || []
        );

        // Buscar todas as conquistas (colunas específicas)
        const { data: allAchievements, error } = await supabase
            .from('achievements')
            .select('id, icon, name, description, rarity, unlock_condition')
            .order('sort_order', { ascending: true })
            .limit(6);

        if (error) throw error;

        // Formatar para o formato do ProfileScreen
        return allAchievements?.map(a => {
            const userAch = unlockedMap.get(a.id);
            const isUnlocked = !!userAch;
            const condition = a.unlock_condition || {};

            return {
                id: a.id,
                icon: a.icon,
                name: a.name,
                desc: a.description,
                unlocked: isUnlocked,
                rarity: a.rarity,
                date: isUnlocked ? new Date(userAch.unlocked_at).toLocaleDateString('pt-BR') : null,
                progress: isUnlocked ? 100 : (userAch?.progress || 0),
                current: condition.current || 0,
                target: condition.target || 100,
                percentage: a.rarity === 'epic' ? 5 : (a.rarity === 'rare' ? 15 : null),
            };
        }) || [];
    } catch (error) {

        return [];
    }
};

/**
 * Desbloqueia uma conquista para o usuário
 */
export const unlockAchievement = async (achievementCode) => {
    try {
        const user = await getCachedUser();
        if (!user) return { success: false };

        // Buscar achievement por código
        const { data: achievement } = await supabase
            .from('achievements')
            .select('id, xp_reward, name')
            .eq('code', achievementCode)
            .single();

        if (!achievement) return { success: false, error: 'Achievement not found' };

        // Inserir desbloqueio
        const { error } = await supabase
            .from('user_achievements')
            .insert({
                user_id: user.id,
                achievement_id: achievement.id,
                progress: 100,
            });

        if (error && error.code !== '23505') throw error; // Ignora duplicatas

        // Criar notificação
        await createNotification({
            type: 'achievement',
            title: '🏆 Nova Conquista!',
            message: `Você desbloqueou "${achievement.name}"`,
            icon: '🏆',
        });

        // Adicionar XP
        await addXPToProfile(achievement.xp_reward);

        return { success: true, achievement };
    } catch (error) {

        return { success: false, error: error.message };
    }
};

// ==================== ATIVIDADES DO USUÁRIO ====================

/**
 * Obtém atividades recentes do usuário para o feed do perfil
 * Retorna no formato do mock ACTIVITIES
 */
export const getUserActivities = async (limit = 5) => {
    try {
        const user = await getCachedUser();
        if (!user) return [];

        const { data, error } = await supabase
            .from('user_activities')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(limit);

        if (error) throw error;

        // Formatar para o formato do ProfileScreen
        return data?.map(activity => ({
            id: activity.id,
            type: activity.activity_type,
            icon: activity.icon,
            text: activity.text,
            time: formatTimeAgo(activity.created_at),
            badge: activity.badge,
            badgeType: activity.badge_type,
        })) || [];
    } catch (error) {

        return [];
    }
};

/**
 * Registra uma nova atividade
 */
export const recordActivity = async (activityData) => {
    try {
        const user = await getCachedUser();
        if (!user) return { success: false };

        const { error } = await supabase
            .from('user_activities')
            .insert({
                user_id: user.id,
                activity_type: activityData.type,
                icon: activityData.icon,
                text: activityData.text,
                badge: activityData.badge,
                badge_type: activityData.badgeType,
                reference_id: activityData.referenceId,
            });

        if (error) throw error;
        return { success: true };
    } catch (error) {

        return { success: false };
    }
};

// ==================== NOTIFICAÇÕES ====================

/**
 * Obtém notificações do usuário
 */
export const getNotifications = async (unreadOnly = false) => {
    try {
        const user = await getCachedUser();
        if (!user) return [];

        let query = supabase
            .from('notifications')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(20);

        if (unreadOnly) {
            query = query.eq('is_read', false);
        }

        const { data, error } = await query;
        if (error) throw error;

        return data?.map(n => ({
            id: n.id,
            type: n.type,
            title: n.title,
            message: n.message,
            icon: n.icon,
            isRead: n.is_read,
            createdAt: n.created_at,
            time: formatTimeAgo(n.created_at),
            data: n.data,
        })) || [];
    } catch (error) {

        return [];
    }
};

/**
 * Conta notificações não lidas
 */
export const getUnreadNotificationCount = async () => {
    try {
        const user = await getCachedUser();
        if (!user) return 0;

        // Otimizado: contagem local em vez de HEAD
        const { data, error } = await supabase
            .from('notifications')
            .select('id')
            .eq('user_id', user.id)
            .eq('is_read', false)
            .limit(100);

        if (error) throw error;
        return data?.length || 0;
    } catch (error) {

        return 0;
    }
};

/**
 * Marca notificação como lida
 */
export const markNotificationAsRead = async (notificationId) => {
    try {
        const { error } = await supabase
            .from('notifications')
            .update({ is_read: true, read_at: new Date().toISOString() })
            .eq('id', notificationId);

        if (error) throw error;
        return { success: true };
    } catch (error) {

        return { success: false };
    }
};

/**
 * Marca todas notificações como lidas
 */
export const markAllNotificationsAsRead = async () => {
    try {
        const user = await getCachedUser();
        if (!user) return { success: false };

        const { error } = await supabase
            .from('notifications')
            .update({ is_read: true, read_at: new Date().toISOString() })
            .eq('user_id', user.id)
            .eq('is_read', false);

        if (error) throw error;
        return { success: true };
    } catch (error) {

        return { success: false };
    }
};

/**
 * Cria uma nova notificação
 */
export const createNotification = async (notificationData) => {
    try {
        const user = await getCachedUser();
        if (!user) return { success: false };

        const { error } = await supabase
            .from('notifications')
            .insert({
                user_id: user.id,
                type: notificationData.type,
                title: notificationData.title,
                message: notificationData.message,
                icon: notificationData.icon || '🔔',
                data: notificationData.data || {},
            });

        if (error) throw error;
        return { success: true };
    } catch (error) {

        return { success: false };
    }
};

// ==================== SESSÕES DE ESTUDO ====================

/**
 * Inicia uma nova sessão de estudo
 */
export const startStudySession = async (sessionData) => {
    try {
        const user = await getCachedUser();
        if (!user) return { success: false };

        const { data, error } = await supabase
            .from('study_sessions')
            .insert({
                user_id: user.id,
                session_type: sessionData.type || 'practice',
                category: sessionData.category,
                banca: sessionData.banca,
                metadata: sessionData.metadata || {},
            })
            .select()
            .single();

        if (error) throw error;
        return { success: true, session: data };
    } catch (error) {

        return { success: false };
    }
};

/**
 * Finaliza uma sessão de estudo
 */
export const endStudySession = async (sessionId, results) => {
    try {
        const { error } = await supabase
            .from('study_sessions')
            .update({
                ended_at: new Date().toISOString(),
                duration_seconds: results.durationSeconds,
                questions_answered: results.questionsAnswered,
                questions_correct: results.questionsCorrect,
                xp_earned: results.xpEarned,
                is_completed: true,
            })
            .eq('id', sessionId);

        if (error) throw error;
        return { success: true };
    } catch (error) {

        return { success: false };
    }
};

/**
 * Obtém sessões de estudo recentes
 */
export const getRecentStudySessions = async (limit = 10) => {
    try {
        const user = await getCachedUser();
        if (!user) return [];

        const { data, error } = await supabase
            .from('study_sessions')
            .select('*')
            .eq('user_id', user.id)
            .eq('is_completed', true)
            .order('started_at', { ascending: false })
            .limit(limit);

        if (error) throw error;
        return data || [];
    } catch (error) {

        return [];
    }
};

// ==================== OBJETIVOS DO USUÁRIO ====================

/**
 * Obtém objetivo ativo do usuário
 * Retorna no formato do mock NEXT_GOAL
 */
export const getActiveGoal = async () => {
    try {
        const user = await getCachedUser();
        if (!user) return null;

        const { data, error } = await supabase
            .from('user_goals')
            .select('*')
            .eq('user_id', user.id)
            .eq('is_active', true)
            .eq('is_completed', false)
            .order('created_at', { ascending: false })
            .limit(1)
            .single();

        if (error && error.code !== 'PGRST116') throw error;

        if (!data) return null;

        return {
            id: data.id,
            icon: data.icon,
            name: data.name,
            desc: data.description,
            current: data.current,
            target: data.target,
            progress: Math.round((data.current / data.target) * 100),
        };
    } catch (error) {

        return null;
    }
};

/**
 * Atualiza progresso do objetivo
 */
export const updateGoalProgress = async (goalId, newCurrent) => {
    try {
        const { data: goal } = await supabase
            .from('user_goals')
            .select('target')
            .eq('id', goalId)
            .single();

        const isCompleted = newCurrent >= goal?.target;

        const { error } = await supabase
            .from('user_goals')
            .update({
                current: newCurrent,
                is_completed: isCompleted,
                completed_at: isCompleted ? new Date().toISOString() : null,
            })
            .eq('id', goalId);

        if (error) throw error;
        return { success: true, isCompleted };
    } catch (error) {

        return { success: false };
    }
};

// ==================== PERFIL E GAMIFICAÇÃO ====================

/**
 * Obtém dados completos do perfil para o ProfileScreen
 * Retorna no formato dos mocks USER_DATA e STATS
 */
export const getProfileData = async () => {
    try {
        const user = await getCachedUser();
        if (!user) return null;

        const { data: profile, error } = await supabase
            .from('profiles')
            .select('id, nome, username, avatar_seed, foto_url, bio, is_verified, is_premium, streak_atual, streak_maximo, nivel, xp_total, questoes_respondidas, questoes_corretas, total_study_time_minutes, followers_count, created_at, last_study_date')
            .eq('id', user.id)
            .single();

        if (error) throw error;

        // Contar conquistas desbloqueadas (otimizado: sem HEAD)
        const { data: achievementsData } = await supabase
            .from('user_achievements')
            .select('id')
            .eq('user_id', user.id)
            .limit(100);
        const achievementsCount = achievementsData?.length || 0;

        // Formatar USER_DATA
        const userData = {
            name: profile.nome || 'Usuário',
            username: profile.username || user.email?.split('@')[0] || 'user',
            avatarSeed: profile.avatar_seed || 'happy',
            fotoUrl: profile.foto_url || null,
            bio: profile.bio || 'Estudante de concursos públicos focado na aprovação! 🚀',
            isVerified: profile.is_verified || false,
            isPremium: profile.is_premium || false,
            memberSince: new Date(profile.created_at).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' }),
            status: 'online',
            streak: {
                current: profile.streak_atual || 0,
                progress: Math.min(100, ((profile.streak_atual || 0) / 30) * 100),
            },
        };

        // Formatar STATS
        const stats = {
            questionsAnswered: profile.questoes_respondidas || 0,
            accuracyRate: profile.questoes_respondidas > 0
                ? Math.round((profile.questoes_corretas / profile.questoes_respondidas) * 100)
                : 0,
            totalStudyTime: formatStudyTime(profile.total_study_time_minutes || 0),
            globalRank: 127, // Pode ser calculado depois
            rankChange: 23,
            followers: profile.followers_count || 0,
            achievements: achievementsCount || 0,
        };

        return { userData, stats, profile };
    } catch (error) {

        return null;
    }
};

/**
 * Adiciona XP ao perfil — server-side via RPC award_xp (correção #1b:
 * cliente não escreve mais xp_total/nivel direto; a coluna está trancada
 * por trigger desde a migration 20260610160000).
 */
const addXPToProfile = async (amount) => {
    try {
        const user = await getCachedUser();
        if (!user) return;

        await supabase.rpc('award_xp', { p_amount: amount, p_reason: 'achievement' });
    } catch (error) {

    }
};

// ==================== UTILITÁRIOS ====================

/**
 * Formata tempo de estudo
 */
const formatStudyTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
        return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
};

/**
 * Formata "há X tempo"
 */
const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'agora';
    if (diffMins < 60) return `há ${diffMins} min`;
    if (diffHours < 24) return `há ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
    if (diffDays < 7) return `há ${diffDays} dia${diffDays > 1 ? 's' : ''}`;
    return date.toLocaleDateString('pt-BR');
};

// ==================== GAMIFICAÇÃO (HOMESCREEN) ====================

/**
 * Obtém estatísticas de gamificação do Supabase para HomeScreen
 * Retorna no mesmo formato do gamificationService
 */
export const getGamificationStatsFromDB = async (userId = null) => {
    try {
        // userId da sessão evita supabase.auth.getUser() (rede) no caminho de
        // load da Home — mesmo risco de spinner travado que houve na Performance.
        const uid = userId ?? (await getCachedUser())?.id;
        if (!uid) return null;

        const { data: profile, error } = await supabase
            .from('profiles')
            .select('xp_total, nivel, streak_atual, streak_maximo, last_study_date, daily_goal, daily_answered, daily_correct')
            .eq('id', uid)
            .single();

        if (error) throw error;

        const xpTotal = profile.xp_total || 0;
        const level = profile.nivel || 1;
        const xpForCurrentLevel = (level - 1) * 500;
        const xpForNextLevel = level * 500;
        const xpInCurrentLevel = xpTotal - xpForCurrentLevel;
        const xpNeededForNext = xpForNextLevel - xpForCurrentLevel;
        const progress = Math.round((xpInCurrentLevel / xpNeededForNext) * 100);

        // Títulos baseados no nível
        const titles = [
            { level: 1, title: 'Iniciante', icon: '🌱' },
            { level: 3, title: 'Estudante', icon: '📚' },
            { level: 5, title: 'Dedicado', icon: '⭐' },
            { level: 10, title: 'Expert', icon: '🏆' },
            { level: 15, title: 'Mestre', icon: '👑' },
            { level: 20, title: 'Lenda', icon: '🔥' },
        ];
        const currentTitle = titles.reverse().find(t => level >= t.level) || titles[0];

        return {
            streak: {
                current: profile.streak_atual || 0,
                longest: profile.streak_maximo || 0,
                lastStudyDate: profile.last_study_date,
            },
            xp: {
                total: xpTotal,
                level: level,
                title: currentTitle.title,
                icon: currentTitle.icon,
                progress: progress,
                xpToNextLevel: xpNeededForNext - xpInCurrentLevel,
            },
            dailyChallenge: {
                questionsGoal: profile.daily_goal || 10,
                questionsAnswered: profile.daily_answered || 0,
                correctAnswers: profile.daily_correct || 0,
                completed: (profile.daily_answered || 0) >= (profile.daily_goal || 10),
                progress: Math.min(100, Math.round(((profile.daily_answered || 0) / (profile.daily_goal || 10)) * 100)),
                remaining: Math.max(0, (profile.daily_goal || 10) - (profile.daily_answered || 0)),
            },
        };
    } catch (error) {

        return null;
    }
};

/**
 * Obtém estatísticas semanais do Supabase
 */
export const getWeeklyStatsFromDB = async (userId = null) => {
    try {
        const uid = userId ?? (await getCachedUser())?.id;
        if (!uid) return { total: 0, correct: 0, wrong: 0, percentage: 0, studyDays: [] };

        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

        const { data, error } = await supabase
            .from('answer_history')
            .select('is_correct, answered_at')
            .eq('user_id', uid)
            .gte('answered_at', sevenDaysAgo.toISOString())
            .limit(500); // Limite otimizado para 7 dias

        if (error) throw error;

        const total = data?.length || 0;
        const correct = data?.filter(d => d.is_correct).length || 0;
        const wrong = total - correct;
        const percentage = total > 0 ? Math.round((correct / total) * 100) : 0;

        // Dias da semana estudados
        const studyDays = new Set();
        data?.forEach(d => {
            const date = new Date(d.answered_at);
            studyDays.add(date.getDay());
        });

        return {
            total,
            correct,
            wrong,
            percentage,
            studyDays: Array.from(studyDays),
        };
    } catch (error) {

        return { total: 0, correct: 0, wrong: 0, percentage: 0, studyDays: [] };
    }
};

/**
 * Obtém desafio diário do Supabase
 */
export const getDailyChallenge = async () => {
    try {
        const user = await getCachedUser();
        if (!user) return null;

        const { data: profile } = await supabase
            .from('profiles')
            .select('daily_goal, daily_answered, daily_correct')
            .eq('id', user.id)
            .single();

        const goal = profile?.daily_goal || 10;
        const answered = profile?.daily_answered || 0;
        const correct = profile?.daily_correct || 0;

        return {
            questionsGoal: goal,
            questionsAnswered: answered,
            correctAnswers: correct,
            completed: answered >= goal,
            progress: Math.min(100, Math.round((answered / goal) * 100)),
            remaining: Math.max(0, goal - answered),
        };
    } catch (error) {

        return null;
    }
};

// ==================== DESEMPENHO (PERFORMANCESCREEN) ====================

/**
 * Busca as linhas de answer_history do usuário logado dentro do período e as
 * devolve normalizadas no MESMO shape do "answer log" local do storageService
 * (`{ timestamp:number, isCorrect:boolean, category:string }`).
 *
 * Esta é a fonte ÚNICA da PerformanceScreen quando autenticado: as 4 métricas
 * (totais, disciplinas, histórico, dificuldade) são derivadas DESTAS linhas
 * pelas mesmas funções puras do storageService — garantindo (a) consistência
 * interna entre os blocos da tela, (b) isolamento por usuário (filtro `user_id`
 * + RLS no banco) e (c) uma única implementação da matemática (DB == local).
 *
 * Lança em erro de propósito: o hook trata o throw como sinal de fallback p/ local.
 */
export const getAnswerHistoryForPeriod = async (period = '30days', userId = null) => {
    // userId vem da sessão já em memória (AuthContext). Evita uma chamada de rede
    // `supabase.auth.getUser()` no caminho de carregamento da tela — que pode
    // pendurar (deadlock do lock de auth do supabase-js no RN) e travar o spinner.
    // Só cai no getCachedUser() se o id não foi fornecido (uso fora da tela).
    const uid = userId ?? (await getCachedUser())?.id;
    if (!uid) return [];

    // Início do período alinhado à MEIA-NOITE — igual a getPeriodStartDate() do
    // storageService — para que o recorte SQL concorde com o filtro JS por período.
    const startDate = new Date();
    startDate.setHours(0, 0, 0, 0);
    switch (period) {
        case 'today': break;
        case '7days': startDate.setDate(startDate.getDate() - 7); break;
        case '15days': startDate.setDate(startDate.getDate() - 15); break;
        case '30days': startDate.setDate(startDate.getDate() - 30); break;
        case 'all': startDate.setTime(0); break; // desde sempre (epoch)
        default: startDate.setDate(startDate.getDate() - 30); break;
    }

    const { data, error } = await supabase
        .from('answer_history')
        .select('is_correct, category, answered_at')
        .eq('user_id', uid)
        .gte('answered_at', startDate.toISOString())
        .order('answered_at', { ascending: false })
        .limit(2000); // teto de segurança; ordenado p/ priorizar o mais recente

    if (error) throw error;

    return (data || []).map((row) => ({
        timestamp: new Date(row.answered_at).getTime(),
        isCorrect: !!row.is_correct,
        category: row.category || 'OUTROS',
    }));
};

// ==================== ATUALIZAR PERFIL ====================

/**
 * Atualiza dados do perfil do usuário no Supabase
 */
export const updateUserProfile = async (profileData) => {
    try {
        const user = await getCachedUser();
        if (!user) throw new Error('Usuário não autenticado');

        const { data, error } = await supabase
            .from('profiles')
            .update({
                nome: profileData.nome,
                username: profileData.username,
                avatar_seed: profileData.avatar_seed,
                foto_url: profileData.foto_url,
                bio: profileData.bio,
                updated_at: new Date().toISOString(),
            })
            .eq('id', user.id)
            // lista explícita: .select() vazio = select=* e falha com 42501
            // desde o grant por coluna em profiles (migration 20260610130000)
            .select('id, nome, username, avatar_seed, foto_url, bio, updated_at')
            .single();

        if (error) throw error;

        return { success: true, data };
    } catch (error) {
        // 23505 = unique_violation. A única UNIQUE em `profiles` é
        // `profiles_username_key`, então a mensagem crua do Postgres
        // ("duplicate key value violates unique constraint ...") chegava
        // inteira ao usuário no FeedbackModal. Traduz aqui, onde se conhece
        // o erro do banco; `code` volta junto p/ quem quiser tratar.
        const message = error?.code === '23505'
            ? 'Esse username já está em uso. Escolha outro.'
            : (error?.message || 'Não foi possível salvar as alterações.');
        return { success: false, error: message, code: error?.code };
    }
};

/**
 * Faz upload de foto de perfil para Supabase Storage
 * Usa expo-file-system para ler arquivo local como base64
 */
export const uploadProfilePhoto = async (imageUri) => {
    try {
        const user = await getCachedUser();
        if (!user) throw new Error('Usuário não autenticado');

        // Criar nome único para arquivo
        const fileName = `${user.id}_${Date.now()}.jpg`;

        // Corpo do upload — o jeito de LER o arquivo muda por plataforma.
        //
        // WEB: `expo-file-system` NÃO existe no navegador. Chamar
        // `readAsStringAsync` ali lança
        // "The method or property expo-file-system.readAsStringAsync is not
        // available on web", que era o erro que o usuário via ao trocar a foto.
        // No navegador o `uri` que chega do ImageManipulator é `data:` ou
        // `blob:` — os dois são legíveis por `fetch`, e o Supabase Storage
        // aceita um Blob direto, sem passar por base64.
        //
        // NATIVO: caminho inalterado (base64 -> ArrayBuffer), já estabilizado.
        let body;
        let contentType = 'image/jpeg';

        if (Platform.OS === 'web') {
            const response = await fetch(imageUri);
            if (!response.ok) {
                throw new Error(`Não foi possível ler a imagem selecionada (${response.status})`);
            }
            const blob = await response.blob();
            // `blob.type` costuma vir preenchido; se vier vazio (alguns
            // navegadores com `data:` URI), mantemos image/jpeg — que é o que o
            // ImageManipulator produz.
            if (blob.type) contentType = blob.type;
            body = blob;
        } else {
            // Usar string 'base64' diretamente (EncodingType pode ser undefined em algumas versões)
            const base64 = await FileSystem.readAsStringAsync(imageUri, {
                encoding: 'base64',
            });

            // Converter base64 para ArrayBuffer (Supabase aceita)
            const binaryString = atob(base64);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            body = bytes.buffer;
        }

        // Upload para Supabase Storage
        const { data, error } = await supabase.storage
            .from('avatars')
            .upload(fileName, body, {
                contentType,
                upsert: true,
            });

        if (error) throw error;

        // Obter URL pública
        const { data: urlData } = supabase.storage
            .from('avatars')
            .getPublicUrl(fileName);

        return { success: true, url: urlData.publicUrl };
    } catch (error) {

        return { success: false, error: error.message };
    }
};

/**
 * Apaga uma foto de perfil do bucket `avatars` a partir da sua URL pública.
 *
 * Por que existe: `uploadProfilePhoto` grava com nome único
 * (`${user.id}_${Date.now()}.jpg`) e nada apagava o arquivo anterior — cada
 * troca de foto (e todo upload abandonado ao fechar o modal sem salvar)
 * deixava um órfão permanente no bucket.
 *
 * Best-effort, mesmo padrão do `communityVideosService`: nunca lança e nunca
 * bloqueia a UI. Se a policy de DELETE do bucket não permitir, o pior caso
 * volta a ser o de hoje (arquivo órfão), nunca um erro na cara do usuário.
 *
 * Seguro por construção: só remove arquivo cujo nome começa com o id do
 * próprio usuário — URL externa ou arquivo de terceiro é ignorado.
 */
export const deleteProfilePhoto = async (photoUrl) => {
    if (!photoUrl) return;
    try {
        const user = await getCachedUser();
        if (!user) return;

        // URL pública: .../storage/v1/object/public/avatars/<fileName>
        const fileName = photoUrl.split('/avatars/').pop()?.split('?')[0];
        if (!fileName || !fileName.startsWith(`${user.id}_`)) return;

        await supabase.storage.from('avatars').remove([fileName]);
    } catch {
        // Órfão é melhor que crash — silencioso de propósito.
    }
};

// ==================== FUNÇÕES OTIMIZADAS (recebem userId diretamente) ====================

/**
 * Versão otimizada de getProfileData - recebe userId diretamente
 * Também paraleliza as queries internas
 */
export const getProfileDataOptimized = async (userId) => {
    try {
        // Executar queries em paralelo (removido HEAD lento, usando contagem local)
        const [profileResult, achievementsResult] = await Promise.all([
            supabase.from('profiles').select('id, nome, username, avatar_seed, foto_url, bio, is_verified, is_premium, streak_atual, nivel, xp_total, questoes_respondidas, questoes_corretas, total_study_time_minutes, followers_count, created_at').eq('id', userId).single(),
            supabase.from('user_achievements').select('id').eq('user_id', userId).limit(100),
        ]);

        if (profileResult.error) throw profileResult.error;
        const profile = profileResult.data;
        const achievementsCount = achievementsResult.data?.length || 0;

        const userData = {
            name: profile.nome || 'Usuário',
            username: profile.username || 'user',
            avatarSeed: profile.avatar_seed || 'happy',
            fotoUrl: profile.foto_url || null,
            bio: profile.bio || 'Estudante de concursos públicos focado na aprovação! 🚀',
            isVerified: profile.is_verified || false,
            isPremium: profile.is_premium || false,
            memberSince: new Date(profile.created_at).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' }),
            status: 'online',
            streak: {
                current: profile.streak_atual || 0,
                progress: Math.min(100, ((profile.streak_atual || 0) / 30) * 100),
            },
        };

        const stats = {
            questionsAnswered: profile.questoes_respondidas || 0,
            accuracyRate: profile.questoes_respondidas > 0
                ? Math.round((profile.questoes_corretas / profile.questoes_respondidas) * 100)
                : 0,
            totalStudyTime: formatStudyTime(profile.total_study_time_minutes || 0),
            globalRank: 127,
            rankChange: 23,
            followers: profile.followers_count || 0,
            achievements: achievementsCount,
        };

        return { userData, stats, profile };
    } catch (error) {
        console.warn('[getProfileDataOptimized] falhou', {
            userId,
            name: error?.name,
            code: error?.code,
            message: error?.message,
        });
        // PGRST116 = perfil ainda não existe → vazio legítimo (conta nova).
        if (error?.code === 'PGRST116') return null;
        // Rede/timeout/RLS/schema → PROPAGA para o React Query orquestrar
        // retry (recupera hang intermitente) e expor isError (UI de erro).
        throw error;
    }
};

/**
 * Versão otimizada de getAchievementsForProfile - recebe userId diretamente
 */
export const getAchievementsForProfileOptimized = async (userId) => {
    try {
        const [userAchievementsResult, allAchievementsResult] = await Promise.all([
            supabase.from('user_achievements').select('achievement_id, unlocked_at').eq('user_id', userId).limit(50),
            supabase.from('achievements').select('id, icon, name, description, rarity, unlock_condition').order('sort_order', { ascending: true }).limit(6),
        ]);

        const unlockedMap = new Map(
            userAchievementsResult.data?.map(ua => [ua.achievement_id, ua]) || []
        );

        return allAchievementsResult.data?.map(a => {
            const userAch = unlockedMap.get(a.id);
            const isUnlocked = !!userAch;
            const condition = a.unlock_condition || {};

            return {
                id: a.id,
                icon: a.icon,
                name: a.name,
                desc: a.description,
                unlocked: isUnlocked,
                rarity: a.rarity,
                date: isUnlocked ? new Date(userAch.unlocked_at).toLocaleDateString('pt-BR') : null,
                progress: isUnlocked ? 100 : (userAch?.progress || 0),
                current: condition.current || 0,
                target: condition.target || 100,
                percentage: a.rarity === 'epic' ? 5 : (a.rarity === 'rare' ? 15 : null),
            };
        }) || [];
    } catch (error) {

        return [];
    }
};

/**
 * Versão otimizada de getUserActivities - recebe userId diretamente
 */
export const getUserActivitiesOptimized = async (userId, limit = 5) => {
    try {
        const { data, error } = await supabase
            .from('user_activities')
            .select('*')
            .eq('user_id', userId)
            .order('created_at', { ascending: false })
            .limit(limit);

        if (error) throw error;

        return data?.map(activity => ({
            id: activity.id,
            type: activity.activity_type,
            icon: activity.icon,
            text: activity.text,
            time: formatTimeAgo(activity.created_at),
            badge: activity.badge,
            badgeType: activity.badge_type,
        })) || [];
    } catch (error) {

        return [];
    }
};

/**
 * Versão otimizada de getActiveGoal - recebe userId diretamente
 */
export const getActiveGoalOptimized = async (userId) => {
    try {
        const { data, error } = await supabase
            .from('user_goals')
            .select('*')
            .eq('user_id', userId)
            .eq('is_active', true)
            .eq('is_completed', false)
            .order('created_at', { ascending: false })
            .limit(1)
            .single();

        if (error && error.code !== 'PGRST116') throw error;
        if (!data) return null;

        return {
            id: data.id,
            icon: data.icon,
            name: data.name,
            desc: data.description,
            current: data.current,
            target: data.target,
            progress: Math.round((data.current / data.target) * 100),
        };
    } catch (error) {

        return null;
    }
};

/**
 * Mapeia o payload do RPC get_profile_screen para os shapes que a ProfileScreen
 * consome. Reusa os mesmos transforms/helpers do caminho antigo → comportamento
 * idêntico (datas/relativos formatados aqui, no locale pt-BR).
 */
const mapProfileScreenPayload = (payload) => {
    const profile = payload.profile;

    const userData = {
        name: profile.nome || 'Usuário',
        username: profile.username || 'user',
        avatarSeed: profile.avatar_seed || 'happy',
        fotoUrl: profile.foto_url || null,
        bio: profile.bio || 'Estudante de concursos públicos focado na aprovação! 🚀',
        isVerified: profile.is_verified || false,
        isPremium: profile.is_premium || false,
        memberSince: new Date(profile.created_at).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' }),
        status: 'online',
        streak: {
            current: profile.streak_atual || 0,
            progress: Math.min(100, ((profile.streak_atual || 0) / 30) * 100),
        },
    };

    const stats = {
        questionsAnswered: profile.questoes_respondidas || 0,
        accuracyRate: profile.questoes_respondidas > 0
            ? Math.round((profile.questoes_corretas / profile.questoes_respondidas) * 100)
            : 0,
        totalStudyTime: formatStudyTime(profile.total_study_time_minutes || 0),
        globalRank: 127, // TODO: ranking real pré-computado (view materializada/job)
        rankChange: 23,
        followers: profile.followers_count || 0,
        achievements: payload.achievements_count || 0,
    };

    const achievements = (payload.achievements || []).map((a) => {
        const condition = a.unlock_condition || {};
        const isUnlocked = !!a.unlocked;
        return {
            id: a.id,
            icon: a.icon,
            name: a.name,
            desc: a.description,
            unlocked: isUnlocked,
            rarity: a.rarity,
            date: isUnlocked && a.unlocked_at ? new Date(a.unlocked_at).toLocaleDateString('pt-BR') : null,
            progress: isUnlocked ? 100 : 0,
            current: condition.current || 0,
            target: condition.target || 100,
            percentage: a.rarity === 'epic' ? 5 : (a.rarity === 'rare' ? 15 : null),
        };
    });

    const activities = (payload.activities || []).map((activity) => ({
        id: activity.id,
        type: activity.activity_type,
        icon: activity.icon,
        text: activity.text,
        time: formatTimeAgo(activity.created_at),
        badge: activity.badge,
        badgeType: activity.badge_type,
    }));

    const goal = payload.goal
        ? {
            id: payload.goal.id,
            icon: payload.goal.icon,
            name: payload.goal.name,
            desc: payload.goal.description,
            current: payload.goal.current,
            target: payload.goal.target,
            progress: payload.goal.target ? Math.round((payload.goal.current / payload.goal.target) * 100) : 0,
        }
        : null;

    const w = payload.weekly || {};
    const weekly = {
        total: w.total || 0,
        correct: w.correct || 0,
        wrong: w.wrong || 0,
        percentage: w.percentage || 0,
        studyDays: w.study_days || [],
    };

    return { userData, stats, achievements, activities, goal, weekly };
};

/**
 * Fallback resiliente: roda o caminho antigo (várias queries em paralelo) caso o
 * RPC get_profile_screen ainda não exista no banco (db push pendente) ou falhe.
 * Retorna o MESMO shape agregado do caminho rápido.
 */
const getProfileScreenFallback = async (userId) => {
    const [data, achievements, activities, goal, weekly] = await Promise.all([
        getProfileDataOptimized(userId),
        getAchievementsForProfileOptimized(userId),
        getUserActivitiesOptimized(userId, 5),
        getActiveGoalOptimized(userId),
        getWeeklyStatsFromDB(userId),
    ]);
    if (data === null) return null;
    return {
        userData: data.userData,
        stats: data.stats,
        achievements,
        activities,
        goal,
        weekly,
    };
};

// Curto-circuito por sessão (igual ao get_feed): após o 1º "função não encontrada",
// não tenta mais o RPC nesta sessão → pré-push sem round-trip falho extra por abertura.
let profileRpcAvailable = true;

const isProfileRpcMissing = (error) => (
    error?.code === 'PGRST202'
    || error?.code === '404'
    || /(could not find|function).*get_profile_screen/i.test(error?.message || '')
);

/**
 * Caminho RÁPIDO da tela de perfil: 1 RPC (1 round-trip) que agrega tudo.
 * Se a RPC ainda não existir, cai no fallback antigo para manter compatibilidade
 * durante deploys. Na web, falhas transitórias são propagadas: disparar cinco
 * consultas logo após um timeout multiplica tráfego e prolonga o loading. O app
 * nativo preserva o fallback anterior, já estabilizado em produção.
 */
export const getProfileScreen = async (userId) => {
    if (!profileRpcAvailable) {
        return getProfileScreenFallback(userId);
    }
    try {
        const { data, error } = await supabase.rpc('get_profile_screen');
        if (error) throw error;
        if (data === null) return null; // conta nova sem profile
        return mapProfileScreenPayload(data);
    } catch (rpcError) {
        if (isProfileRpcMissing(rpcError)) {
            profileRpcAvailable = false;
            if (__DEV__) console.warn('[getProfileScreen] get_profile_screen ausente — fallback até aplicar a migration/reiniciar');
            return getProfileScreenFallback(userId);
        }
        if (Platform.OS !== 'web') return getProfileScreenFallback(userId);
        throw rpcError;
    }
};

// ==================== EXPORTS ====================

export default {
    // Conquistas
    getAllAchievements,
    getUserAchievements,
    getAchievementsForProfile,
    unlockAchievement,

    // Atividades
    getUserActivities,
    recordActivity,

    // Notificações
    getNotifications,
    getUnreadNotificationCount,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    createNotification,

    // Sessões
    startStudySession,
    endStudySession,
    getRecentStudySessions,

    // Objetivos
    getActiveGoal,
    updateGoalProgress,

    // Perfil
    getProfileData,
    updateUserProfile,
    uploadProfilePhoto,
    deleteProfilePhoto,

    // Gamificação (HomeScreen)
    getGamificationStatsFromDB,
    getWeeklyStatsFromDB,
    getDailyChallenge,

    // Performance
    getAnswerHistoryForPeriod,

    // Funções Otimizadas (recebem userId diretamente, sem chamar getUser)
    getProfileDataOptimized,
    getAchievementsForProfileOptimized,
    getUserActivitiesOptimized,
    getActiveGoalOptimized,
};
